<template>
  <div v-if="store">
    <div class="bg-white shadow-sm sm:rounded-lg mt-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 px-4 py-5 sm:px-6">
        <div class="flex justify-center items-center">
          <img :src="store.logoUrl" :alt="store.salesChannel" class="max-w-full h-auto" />
        </div>
        <div>
          <h3 class="px-4 text-lg leading-6 text-gray-900 font-bold">
            {{ store.salesChannel }}
          </h3>
          <div>
            <dl class="px-4 py-5 grid grid-cols-2 gap-x-4 gap-y-8">
              <div>
                <dt class="text-sm font-bold text-gray-500">{{ $t("Id") }}</dt>
                <dd class="mt-1 text-sm text-gray-900">
                  <FieldSpan :style-to-add="'bg-purple-600 text-white'" :value="store.id" />
                </dd>
              </div>
              <div>
                <dt class="text-sm font-bold text-gray-500">
                  {{ $t("Organization name") }}
                </dt>
                <dd class="mt-1 text-sm text-gray-900">
                  {{ store.organization?.name }}
                </dd>
              </div>
              <div>
                <dt class="text-sm font-bold text-gray-500">
                  {{ $t("Source") }}
                </dt>
                <dd class="mt-1 text-sm text-gray-900">
                  {{ $t(`store.source.${store.source}`) }}
                </dd>
              </div>
              <div>
                <dt class="text-sm font-bold text-gray-500">
                  {{ $t("Public subscription link") }}
                </dt>
                <dd class="mt-1 text-sm text-gray-900 flex items-center">
                  <a
                    :href="store.publicSubscriptionLink"
                    target="_blank"
                    class="text-primary-dark underline mr-2 break-words w-full"
                  >
                    {{ store.publicSubscriptionLink }}
                  </a>
                  <ClipboardIcon
                    class="cursor-pointer w-8 h-8 text-primary-dark"
                    @click="copyToClipboard(store.publicSubscriptionLink)"
                  />
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>

    <!-- Performance Monitoring Section (only visible if source is offline) -->
    <div v-if="isOfflineStore" class="bg-white shadow-sm sm:rounded-lg mt-6">
      <div class="flex justify-between">
        <h3 class="px-4 py-4 text-lg leading-6 text-gray-900 font-bold">
          {{ $t("Performance monitoring") }}
        </h3>
        <div class="px-4 flex items-center cursor-pointer" @click="editingPerformance = true">
          <p class="text-sm text-primary-dark underline">Modifier le potentiel de ventes mensuel</p>
          <PencilIcon class="ml-2 cursor-pointer w-6 h-6 text-primary-dark" />
          <Modal v-model="editingPerformance" :open="editingPerformance" @close="editingPerformance = false">
            <StoreEditMonthlySalesTargetForm :store="store" @updated="updated" />
          </Modal>
        </div>
      </div>
      <div class="px-4 py-5 grid grid-cols-3 gap-x-4 gap-y-8">
        <div>
          <dt class="text-sm font-bold text-gray-500">
            {{ $t("Monthly sales target") }}
          </dt>
          <dd class="mt-1 text-sm text-gray-900">
            {{ store.monthlySalesTarget }}
          </dd>
        </div>
      </div>
    </div>

    <div class="bg-white shadow-sm sm:rounded-lg mt-6">
      <div class="flex justify-between">
        <h3 class="px-4 py-4 text-lg leading-6 text-gray-900 font-bold">
          {{ $t("Invoicing") }}
        </h3>
        <div v-if="canEditInvoicing" class="px-4 flex items-center cursor-pointer" @click="editingInvoicing = true">
          <p class="text-sm text-primary-dark underline">Modifier</p>
          <PencilIcon class="ml-2 cursor-pointer w-6 h-6 text-primary-dark" />
        </div>
      </div>
      <div class="px-4 py-5 grid grid-cols-3 gap-x-4 gap-y-8">
        <div>
          <dt class="text-sm font-bold text-gray-500">
            {{ $t("Billing entity") }}
          </dt>
          <dd class="mt-1 text-sm text-gray-900">
            {{ billingBusinessName }}
          </dd>
        </div>
        <div>
          <dt class="text-sm font-bold text-gray-500">
            {{ $t("Billing address") }}
          </dt>
          <dd class="mt-1 text-sm text-gray-900">
            {{ billingAddress }}
          </dd>
        </div>
        <div v-if="isStoreInvoiceLevel">
          <dt class="text-sm font-bold text-gray-500">
            {{ $t("Iban") }}
          </dt>
          <dd class="mt-1 text-sm text-gray-900">
            {{ store.iban?.value || "—" }}
          </dd>
        </div>
        <div v-if="store.advanceMonthsCommission">
          <dt class="text-sm font-bold text-gray-500">
            {{ $t("Advance commission") }}
          </dt>
          <dd class="mt-1 text-sm text-gray-900">
            {{ $t("Yes") }}
          </dd>
        </div>
        <div v-if="isOfflineStore">
          <dt class="text-sm font-bold text-gray-500">Adresse postale</dt>
          <dd class="mt-1 text-sm text-gray-900">
            {{ formatLocation(store.shippingAddress) }}
          </dd>
        </div>
        <div>
          <dt class="text-sm font-bold text-gray-500">Identifiant externe</dt>
          <dd class="mt-1 text-sm text-gray-900">
            {{ store.externalId || "—" }}
          </dd>
        </div>
      </div>
    </div>

    <!-- Discount Section (only visible if source is offline) -->
    <div v-if="isOfflineStore && store.discount" class="bg-white shadow-sm sm:rounded-lg mt-6">
      <h3 class="px-4 py-4 text-lg leading-6 text-gray-900 font-bold">
        {{ $t("Discount") }}
      </h3>
      <div class="px-4 py-5 grid grid-cols-1 gap-x-4 gap-y-8">
        <div>
          <dt class="text-sm font-bold text-gray-500">
            {{ $t("Special offer for your customers") }}
          </dt>
          <dd class="mt-1 text-sm text-gray-900">
            {{ store.discount.title }}
          </dd>
        </div>
      </div>
    </div>

    <Modal :open="editingInvoicing" @close="editingInvoicing = false">
      <StoreEditForm :store="store" @updated="updated" />
    </Modal>
  </div>
  <div v-else>
    <p>{{ $t("Loading...") }}</p>
  </div>
</template>

<script>
import StoreEditForm from "@/components/store/StoreEditForm.vue";
import StoreEditMonthlySalesTargetForm from "@/components/store/StoreEditMonthlySalesTargetForm.vue";
import { Modal, FieldSpan, formatLocation } from "@estaly/ui";
import { ClipboardIcon, PencilIcon } from "@heroicons/vue/solid";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    FieldSpan,
    StoreEditForm,
    StoreEditMonthlySalesTargetForm,
    ClipboardIcon,
    PencilIcon,
    Modal,
  },
  props: ["store"],
  computed: {
    ...mapGetters("store", {
      isOfflineStoreGetter: "isOfflineStore",
      isStoreInvoiceLevelGetter: "isStoreInvoiceLevel",
      canEditInvoicingGetter: "canEditInvoicing",
      billingBusinessNameGetter: "billingBusinessName",
      billingAddressGetter: "billingAddress",
    }),
    isOfflineStore() {
      return this.isOfflineStoreGetter(this.store);
    },
    isStoreInvoiceLevel() {
      return this.isStoreInvoiceLevelGetter(this.store);
    },
    billingBusinessName() {
      return this.billingBusinessNameGetter(this.store);
    },
    billingAddress() {
      return this.billingAddressGetter(this.store);
    },
    canEditInvoicing() {
      return this.canEditInvoicingGetter(this.store);
    },
  },
  data() {
    return {
      editingInvoicing: false,
      editingPerformance: false,
    };
  },
  methods: {
    formatLocation,
    ...mapActions("notifications", ["notify"]),
    updated() {
      this.editingInvoicing = false;
      this.editingPerformance = false;
      this.$emit("reloadStore");
    },
    copyToClipboard() {
      navigator.clipboard
        .writeText(this.store.publicSubscriptionLink)
        .then(() => {
          this.notify({
            category: "simple",
            type: "success",
            title: "Link copied to clipboard!",
          });
        })
        .catch((error) => {
          console.error("Error when copying to clipboard : ", error);
          this.notify({
            category: "simple",
            type: "error",
          });
        });
    },
  },
};
</script>
